import React, { useState } from 'react';
import _ from "lodash";
import { Button, Flex, Modal, Spin, Tooltip, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useTranslation } from "react-i18next";

import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { usePesticides } from "./usePesticides";
import {
  useLazyGetPesticidesListQuery,
  useReportPesticidesMutation
} from "../../../../redux/api/chemicalization/pesticides";
import { useGetDataOnScroll } from "../../../../components/getDataOnScrollProps/useGetDataOnScrollProps";
import { EntPesticides } from "../../../../models/chemicalization/pesticides";
import { EntPesticidesFilter } from "../../../../models/filters/pesticidesFilter";
import { EmptyData } from "../../../../components/empty/emptyData";
import { PageHeader } from "../../../../components/page-header";
import { ContentPartFilter } from "../../../../components/content-part/ContentPartFilter";
import { FilterPesticides } from "./FilterPesticides";
import { AddPesticidesModal } from "./AddPesticides";
import i18n from "i18next";
import ReportChemicalization from "../reportChemicalization/ReportChemicalization";

enum FormField {
  periodStart,
  periodEnd,
  pesticidesTypeId,
  territorialUnitIds,
}

export const Pesticides = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [selectedItem, setSelectedItem] = useState<EntPesticides>(new EntPesticides());
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [form] = useForm();
  const { t } = useTranslation()
  const {
	onAddPesticides, onDeletePesticides, onEditPesticides
  } = usePesticides(modal, () => {
	searchHandleClick();
  })
  const [height, setHeight] = useState<number>(0);

  const storedData = JSON.parse(localStorage.getItem("availableRoutes") || "[]");
  const isChemAvailable = storedData.some((route: any) => (route.alias === "/departments/chemicalization/report" || route.alias === "/interactive-map-ws") && route.methodPost);

  const [getReportPesticides] = useReportPesticidesMutation();
  const [getArchiveFolders] = useLazyGetPesticidesListQuery();

  const {
	filter,
	fetching,
	data,
	access,
	totalItems,
	setData,
	setFetching,
	setFilter,
	scrollHandler,
	setCurrentPage
  } = useGetDataOnScroll<EntPesticides, EntPesticidesFilter>({
	initialFilter: new EntPesticidesFilter(),
	query: getArchiveFolders
  });
  const openAddModal = (item: EntPesticides) => {
	setSelectedItem(item);
	setShowModal(true);
  };
  const setEntityFromFields = (): EntPesticidesFilter => {
	let filt: EntPesticidesFilter = _.clone(filter);
	filt.periodStart = form.getFieldValue(FormField.periodStart)?.format("YYYY-MM-DD") ?? null;
	filt.periodEnd = form.getFieldValue(FormField.periodEnd)?.format("YYYY-MM-DD") ?? null;
	filt.pesticidesTypeId = form.getFieldValue(FormField.pesticidesTypeId) ?? null;
	filt.territorialUnitIds = form.getFieldValue(FormField.territorialUnitIds) ?? null;
	filt.page = 1;
	return filt;
  }
  const clearFields = () => {
	form.setFields([
	  { name: FormField.periodStart, value: null },
	  { name: FormField.periodEnd, value: null },
	  { name: FormField.pesticidesTypeId, value: null },
	  { name: FormField.territorialUnitIds, value: null },
	]);
  }

  const searchHandleClick = () => {
	setFilter(setEntityFromFields());
	setData([]);
	setCurrentPage(0);
	setFetching(true);
  }

  const getReport = async (payload: { periodStart: string; periodEnd: string; language: string }): Promise<Blob> => {
	return await getReportPesticides(payload).unwrap();
  };

  const openReportModal = () => {
	setShowReportModal(true);
  };


  const table = (
	<div style={ { maxHeight: `${ height }px`, overflow: 'auto' } } onScroll={ scrollHandler }>
	  <table>
		<thead>
		<tr>
		  <th style={ { width: "40px" } }>#</th>
		  <th>{ t('titles.territorialUnitRegion') }</th>
		  <th>{ t('titles.types_of_pesticides') }</th>
		  <th>{ t('chemicalization.register_of_pesticides.needed_tonnes') }</th>
		  <th>{ t('chemicalization.register_of_pesticides.supplied_tonnes') }</th>
		  <th>{ t('chemicalization.register_of_pesticides.supply_percent') }</th>
		  <th style={ { width: "120px" } }></th>
		</tr>
		</thead>
		<tbody>
		{ fetching ? (
		  <tr>
			<th colSpan={ 7 }>
			  <Spin spinning={ fetching } style={ {
				width: '100%',
				height: '180px',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
			  } }/>
			</th>
		  </tr>
		) : null }
		{ data.length !== 0 ? data.map((item, index) => (
		  <tr key={ item.id }>
			<td>{ ++index }</td>
			<td>{ i18n.language === 'ru' ? item.territorialUnits?.nameRu : item.territorialUnits?.nameKy }</td>
			<td>
			  <div className="chem-detail">
				{ item?.pesticideDetails?.map((detail) => (
				  <div key={ detail.id }>
					{ i18n.language === 'ru' ? detail?.pesticidesType?.nameRu : detail?.pesticidesType?.nameKy }
				  </div>
				)) }
			  </div>
			</td>
			<td>
			  <div className="chem-detail">
				{ item?.pesticideDetails?.map((detail) => (
				  <div key={ detail.id }>
					{ detail.neededTonnes }
				  </div>
				)) }
			  </div>
			</td>
			<td>
			  <div className="chem-detail">
				{ item?.pesticideDetails?.map((detail) => (
				  <div key={ detail.id }>
					{ detail.suppliedTonnes }
				  </div>
				)) }
			  </div>
			</td>
			<td>
			  <div className="chem-detail">
				{ item?.pesticideDetails?.map((detail) => (
				  <div key={ detail.id }>
					{ Number(detail.supplyPercent).toFixed(3) }
				  </div>
				)) }
			  </div>
			</td>
			<td>
			  <Flex gap="small">
				{ access && access.methodPut && (
				  <Tooltip title={ t('general.edit') }>
					<Button className='btn_edit' onClick={ () => {
					  openAddModal(item)
					} } type="primary" shape="circle" icon={ <EditOutlined/> }/>
				  </Tooltip>
				) }
				{ access && access.methodDelete && (
				  <Tooltip title={ t('general.delete') }>
					<Button className='btn_edit' danger onClick={ () => {
					  onDeletePesticides(item)
					} } type="primary" shape="circle" icon={ <DeleteOutlined/> }/>
				  </Tooltip>
				) }
			  </Flex>
			</td>
		  </tr>
		)) : (
		  <tr>
			<td colSpan={ 9 }>
			  <EmptyData/>
			</td>
		  </tr>
		) }
		</tbody>
	  </table>
	</div>
  );

  return (
	<>
	  <PageHeader
		title={ t('titles.register_of_pesticides') }
		additionalInfo={
		  <Typography.Text style={ { marginBottom: '0.5em', display: 'block' } } type="secondary">
			{ t('chemicalization.register_of_pesticides.types_of_pesticides_desc') } { t('general.records_found') } { totalItems }
		  </Typography.Text>
		}
	  />
	  { access && access.methodPost && (
		<Button type="primary" icon={ <PlusOutlined/> } onClick={ () => {
		  openAddModal(new EntPesticides())
		} }>
		  { t('general.add') }
		</Button>
	  ) }
	  { isChemAvailable && (
		<Button style={ { marginLeft: '5px' } } type="primary" icon={ <PlusOutlined/> }
				onClick={ () => openReportModal() }>
		  { t('general.generate_report') }
		</Button>
	  ) }
	  <ContentPartFilter
		filter={ <FilterPesticides
		  onSearch={ searchHandleClick }
		  onReset={ () => {
			clearFields();
			searchHandleClick()
		  } }
		  formField={ FormField }
		  form={ form }
		/> }
		setHeight={ setHeight }
		table={ table }
	  />
	  <AddPesticidesModal
		pesticides={ selectedItem }
		show={ showModal }
		onHide={ () => {
		  setShowModal(false)
		} }
		onSubmit={ selectedItem.id !== null ? onEditPesticides : onAddPesticides }
	  />
	  <ReportChemicalization
		title={ t('reports.report_by_pesticides') }
		getReport={ getReport }
		show={ showReportModal }
		onHide={ () => setShowReportModal(false) }
	  />
	  { contextHolder }
	</>
  );
}
